import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

export interface PersonaFormData {
  slug: string;
  name: string;
  display_title: string;
  avatar_type: string;
  avatar_key: string;
  voice_provider: string;
  voice_id: string;
  domains: string;          // comma-separated in the form
  system_prompt: string;
  tools_allowed: string;    // comma-separated in the form
  is_active: boolean;
  sort_order: number;
}

interface Props {
  /** null = create mode, non-null slug = edit mode */
  editSlug: string | null;
  initialData?: Partial<PersonaFormData>;
  onSave: (data: PersonaFormData) => void;
  onClose: () => void;
  isSaving: boolean;
  saveError: string | null;
}

const SLOT_HINTS = `Available injection slots:
  {user_name}                  — the user's first name
  {market_outlook_injection}   — latest market outlook summary
  {portfolio_summary_injection}— user's open positions
  {task_state_injection}       — current task context`;

const PLACEHOLDER = `You are {name}, a {display_title} for {user_name}.

Your role: …

{market_outlook_injection}
{portfolio_summary_injection}
{task_state_injection}`;

const DEFAULT_FORM: PersonaFormData = {
  slug: '',
  name: '',
  display_title: '',
  avatar_type: '2d',
  avatar_key: '',
  voice_provider: 'agentvibes',
  voice_id: '',
  domains: '',
  system_prompt: '',
  tools_allowed: '',
  is_active: true,
  sort_order: 0,
};

const PersonaFormModal: React.FC<Props> = ({
  editSlug,
  initialData,
  onSave,
  onClose,
  isSaving,
  saveError,
}) => {
  const isEdit = editSlug !== null;
  const [form, setForm] = useState<PersonaFormData>({ ...DEFAULT_FORM, ...initialData });

  // Reset when initial data changes (edit vs create)
  useEffect(() => {
    setForm({ ...DEFAULT_FORM, ...initialData });
  }, [editSlug]);

  // Skills file status (edit mode only)
  const { data: skillsStatus } = useQuery<{ exists: boolean; path: string }>({
    queryKey: ['persona-skills-status', editSlug],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const apiUrl = (import.meta.env.VITE_API_URL as string) || 'http://localhost:8001';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${apiUrl}/api/v1/trading/personas/${editSlug}/skills-status`, { headers });
      if (!res.ok) throw new Error('Failed to fetch skills status');
      return res.json();
    },
    enabled: isEdit && !!editSlug,
  });

  const set = (field: keyof PersonaFormData, value: string | boolean | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">
            {isEdit ? `Edit Persona — ${editSlug}` : 'New Persona'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Slug — readonly in edit mode */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Slug {isEdit && <span className="text-amber-400">(permanent — cannot change)</span>}
            </label>
            {isEdit ? (
              <div className="px-3 py-2 bg-slate-900 border border-slate-600 rounded text-slate-400 text-sm font-mono">
                {form.slug}
              </div>
            ) : (
              <input
                type="text"
                value={form.slug}
                onChange={(e) => set('slug', e.target.value)}
                placeholder="my-persona"
                required
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
              />
            )}
          </div>

          {/* Name + Display Title */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="George"
                required
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Display Title *</label>
              <input
                type="text"
                value={form.display_title}
                onChange={(e) => set('display_title', e.target.value)}
                placeholder="Market Analyst"
                required
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          {/* Avatar */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Avatar Type</label>
              <select
                value={form.avatar_type}
                onChange={(e) => set('avatar_type', e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
              >
                <option value="2d">2D</option>
                <option value="3d">3D</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Avatar Key</label>
              <input
                type="text"
                value={form.avatar_key}
                onChange={(e) => set('avatar_key', e.target.value)}
                placeholder="analyst"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          {/* Voice */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Voice Provider</label>
              <input
                type="text"
                value={form.voice_provider}
                onChange={(e) => set('voice_provider', e.target.value)}
                placeholder="agentvibes"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Voice ID</label>
              <input
                type="text"
                value={form.voice_id}
                onChange={(e) => set('voice_id', e.target.value)}
                placeholder="provider-specific ID"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          {/* Domains */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Domains <span className="text-slate-500">(comma-separated)</span>
            </label>
            <input
              type="text"
              value={form.domains}
              onChange={(e) => set('domains', e.target.value)}
              placeholder="equities, forex, macro"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
            />
          </div>

          {/* Tools Allowed */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Tools Allowed <span className="text-slate-500">(comma-separated tool names)</span>
            </label>
            <input
              type="text"
              value={form.tools_allowed}
              onChange={(e) => set('tools_allowed', e.target.value)}
              placeholder="get_market_data, list_strategies, ..."
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
            />
          </div>

          {/* System Prompt */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-slate-400">
                System Prompt * <span className="text-slate-500">(min 50 chars)</span>
              </label>
              <span className={`text-xs ${form.system_prompt.length < 50 ? 'text-amber-400' : 'text-slate-500'}`}>
                {form.system_prompt.length} chars
              </span>
            </div>
            <textarea
              value={form.system_prompt}
              onChange={(e) => set('system_prompt', e.target.value)}
              placeholder={PLACEHOLDER}
              required
              rows={10}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm font-mono focus:outline-none focus:border-teal-500 resize-y"
            />
            <pre className="mt-1 text-xs text-slate-500 whitespace-pre-wrap">{SLOT_HINTS}</pre>
          </div>

          {/* Sort Order + Active */}
          <div className="flex items-center gap-6">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Sort Order</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)}
                className="w-24 px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer mt-4">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
                className="w-4 h-4 accent-teal-500"
              />
              <span className="text-sm text-slate-300">Active</span>
            </label>
          </div>

          {/* Skills file status (edit mode) */}
          {isEdit && skillsStatus && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded text-xs ${
              skillsStatus.exists
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
            }`}>
              {skillsStatus.exists
                ? <><CheckCircle2 className="w-3.5 h-3.5" /> Skills file found: <code>{skillsStatus.path}</code></>
                : <><AlertTriangle className="w-3.5 h-3.5" /> Skills file not found — persona will use system_prompt only</>
              }
            </div>
          )}

          {/* Error */}
          {saveError && (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs">
              {saveError}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-5 py-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
            >
              {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Persona'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonaFormModal;
